import { casOpsApi, CollectionModel, QueryTag, TagList } from '../api';
import { ModelWithId } from './commonModels';

export interface GeofenceStyleDisplayFillModel {
  defaultColour: string; // CSS colour RGB format "#FF0000"
  activeColour: string; // CSS colour RGB format "#FF0000"
  pattern: number; // Fixed list (could be an enum, if sent as number)
  opacity: number; // 0 - 100 in steps of 10 only (dropdown)
}

export interface GeofenceStyleDisplayLineModel {
  colour: string; // CSS colour RGB format "#FF0000"
  pattern: number; // Fixed list (could be an enum, if sent as number)
  opacity: number; // 0 - 100 in steps of 10 only (dropdown)
  width: number; // 0 - 8 inclusive. integers
}

export interface GeofenceStyleUnionModel extends ModelWithId {
  name: string; // Max 128 chars with regex "^[a-zA-Z0-9-_\\.\\s]+$"
  desc: string; // description. Max 255 chars
  used: boolean; // disable delete
  geos: string[]; // Geofence NAMES (not IDs), max of 4, referenced by style
  fill: GeofenceStyleDisplayFillModel;
  line: GeofenceStyleDisplayLineModel;
}

export type ListGeofenceStyleModel = Omit<GeofenceStyleUnionModel, 'fill' | 'line'>;

export type GetGeofenceStyleModel = Omit<GeofenceStyleUnionModel, 'geos'>;

export type CreateGeofenceStyleModel = Omit<GeofenceStyleUnionModel, 'id' | 'used' | 'geos'>;

export type EditGeofenceStyleModel = Omit<GeofenceStyleUnionModel, 'used' | 'geos'>;

export const geofenceStylesApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    listGeofenceStyles: build.query<ListGeofenceStyleModel[], void>({
      query: () => 'api/geofencesettings/styles',
      transformResponse: (response: CollectionModel<ListGeofenceStyleModel>, meta, arg) =>
        response.items,
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.GeofenceStyle, id: TagList }] : [],
    }),
    getGeofenceStyle: build.query<GetGeofenceStyleModel, ModelWithId>({
      query: ({ id }) => `api/geofencesettings/styles/${id}`,
      providesTags: (result, error, { id }) =>
        result ? [{ type: QueryTag.GeofenceStyle, id: id }] : [],
    }),
    createGeofenceStyle: build.mutation<void, CreateGeofenceStyleModel>({
      query: body => ({
        url: 'api/geofencesettings/styles',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: QueryTag.GeofenceStyle, id: TagList }, QueryTag.GeofenceVersion],
    }),
    editGeofenceStyle: build.mutation<void, EditGeofenceStyleModel>({
      query: ({ id, ...body }) => ({
        url: `api/geofencesettings/styles/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.GeofenceStyle, id: TagList },
              { type: QueryTag.GeofenceStyle, id: id },
              QueryTag.GeofenceVersion,
            ]
          : [],
    }),
    deleteGeofenceStyle: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/geofencesettings/styles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.GeofenceStyle, id: TagList },
              { type: QueryTag.GeofenceStyle, id: id },
              QueryTag.GeofenceVersion,
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListGeofenceStylesQuery,
  useGetGeofenceStyleQuery,
  useCreateGeofenceStyleMutation,
  useEditGeofenceStyleMutation,
  useDeleteGeofenceStyleMutation,
} = geofenceStylesApi;
